import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  Typography,
  Button,
  FormHelperText,
  TextField,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import logoImg from "./assets/logo.png";
import signupSuccess from "./assets/signup-success.png";
import mascotJoin from "./assets/login-mascot.png";

import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

const StyledContainer = styled(Container)(() => ({            
    minHeight: 624,
    angle: "0 deg",
    opacity: 1,
    borderRadius: "16px",
    boxShadow: "0px 4px 12px 0px #0000001F",
    display: "flex",
    justifyContent: "center",
    justifyItems: "center" ,
    // DESKTOP
    maxWidth: "1080px",
    margin: "48px 150px",

    "@media (max-width: 1360px)": {
        maxWidth: "802px",      // TABLET WIDTH
        margin: "32px auto",
    },
    "@media (max-width: 856px)": {
        maxWidth: "361px",      // MOBILE WIDTH
        margin: "8px auto",
    },    
}));

const FormSide = styled(Box)(() => ({
  
   width: "540px",             // Desktop 2 columns = 1080 total
   padding: "32px",
   gap: "16px",
   minHeight: 624,
  "@media (max-width: 1360px)": 
  { width: "401px" },// Tablet 2 columns = 802 total
  
  "@media (max-width: 856px)":  
  {  width: "100%",            // Mobile = single column
    padding: "16px",          // Reduce padding on mobile 
    height: "auto",
    minHeight: "auto"
  },  
  angle: "0 deg",
  opacity: 1,
}));

const StyledBox  = styled(Box)(() => ({
    color: "#212121",
    backgroundColor: "#F0FEF4",
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    fontFamily: "Arial",
    lineHeight: 1.4,
}));

const StyledButton = styled(Button)(() => ({
    width: "158px",
    height: "48px",
    backgroundColor: "#EF5658",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#EF5658",
    },
    "&:hover": {
      backgroundColor: "#D33338",
    },
}));

const StyledTypography  = styled(Typography)(() => ({
    color: "rgba(33, 33, 33, 0.8)",
    fontSize: "18px",
    fontStyle: "normal",
    fontFamily: "Roboto,Helvetica,Arial,sans-serif",
    fontWeight: "400",
    lineHeight: "1.7",
    letterSpacing: "0.00938em",
}));



const SignupSuccess = () => {
  const [userEmailAddress, setUseEmailAddress] = useState(null);

  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(856); // Use 856px breakpoint for mobile size

  useEffect(() => {
    setUseEmailAddress(localStorage.getItem("userRegisterEmail"));
    
  }, [localStorage]);

  useEffect(() => {
    window.scrollTo(0, 0);    
  }, []);

  return (
    <Grid>
        <>
          <Helmet>
            <title>R3PLAY - Welcome to R3PLAY!</title>
            <meta
              name="description"
              content="Your account was created successfully. Start exploring toys and stories now"
            />
            <meta
              property="keywords"
              content="Signup success, new user, r3play account"
            />        
          </Helmet>     
              <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }} >            
          
            <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns={!isMobile ? "1fr 1fr" : "1fr"}                 
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children
                  alignItems="start"
                  gap={!isMobile ? "0" : "1"}
                  sx={{ width: "100%" }}
                >
              
                <FormSide sx={{ borderRight: !isMobile ? "1px solid #e0e0e0" : "none" }}>
                    <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Typography gutterBottom 
                          variant={!isMobile && !isTablet ? "h2" : "h3"}
                          fontSize={!isMobile && !isTablet ? "36px" : "30px"}
                          sx={{                          
                          GutterBottom: "False",                          
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: "700",
                          lineHeight: "1.75",
                          marginBottom: "25px",
                          letterSpacing: "0.00938em"
                        }}>
                          Congratulations!
                      </Typography>      
                       
                    </Box>

                    <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                    <Typography gutterBottom 
                         variant ="h4" 
                         fontSize = "24px"                          
                          sx={{                          
                          color: "#666",                          
                          fontFamily: "Arial, sans-serif",                                                    
                          fontWeight: "600",                          
                          marginBottom: "25px",
                          letterSpacing: "0.25px"
                        }}>
                          Welcome aboard! You’re now part of the R3PLAY community.
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="center" >
                        <img src={signupSuccess} alt="logo" width={!isMobile ? "356" : "243"} />
                    </Box>                    
                                     
                  </FormSide>
                  

                  <FormSide sx={{ paddingLeft: "34px" }}>
                    <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Typography gutterBottom 
                          variant={!isMobile && !isTablet ? "h2" : "h3"}
                          fontSize={!isMobile && !isTablet ? "36px" : "30px"}
                          sx={{                          
                          GutterBottom: "False",                          
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: "700",                          
                          marginBottom: "25px",
                          letterSpacing: "0.00938em"
                        }}>
                          Account Created Successfully
                      </Typography>     
                       
                    </Box>

                    <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >

                    <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        
                        }}>
                        
                        Thank you for registering with R3PLAY. 
                        Your user account is created. You will receive an email from 
                        {" "}
                         <a href="mailto:letsplay@r3play.com" target="_self"
                           style={{ color: "red", textDecoration: "none" }}>
                          letsplay@r3play.com
                        </a>{" "}
                        <p>
                          We sent the email to: {userEmailAddress}
                        </p>
                        <p>
                          Please check your inbox. To activate your account, 
                          confirm your email address by clicking the link in the email we sent you.
                        </p>
                        <p>
                          If you did not receive the email in your inbox, 
                          check your spambox. You should have received an email from R3PLAY.
                           If you still cannot find the email, please contact letsplay@r3play.com 
                           and we will help you activate your account.
                        </p>
                        <p>
                          <span style={{ color: 'red' }}>R3PLAY</span> | Relive • Reuse • Replay
                        </p>
                        
                      </Typography>
                    </Box>                 
                  </FormSide>
                </Box>
              </StyledContainer>
              
                {!isTablet && !isMobile
                  ?
                  (
                    <Box
                      sx={{
                        position: "relative",
                        bottom: "-130px",
                        right: 145,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component="img"
                        src={mascotJoin}
                        alt="Join the Fun Mascot Orange"
                        sx={{
                          width: "140px",
                          height: "auto",
                          
                        }}
                      />
                    </Box>
                  )
                  :""
                }
          </Box>
        </>
    </Grid>
  );
};

export default SignupSuccess;
